import React from 'react';
import { BodyTextSmall, ColorProp, DetailTextLarge, FlexCol, FlexRow, H2Text, TextDynamic } from '@minecraft.atoms';
import { FormattedPriceMonth, useTranslation } from '@blocs.i18n';
import { BillingCodes } from '../enums/plans';

export const PlanPriceSection: React.FC<{
  countryCode: string;
  className?: string;
  alignItems?: string;
  isAnnual?: boolean;
  color?: ColorProp;
  highlightPrice?: boolean;
  textAlign?: 'center' | 'left' | 'right';
  title?: string;
  isTrialPeriodEnabled?: boolean;
  billingCode?: string;
  billed?: string;
  currencyCode?: string;
  planTermCode?: string;
  price?: number;
  strikeThroughPrice?: number;
  isMediaPurchase?: boolean;
}> = ({
  countryCode,
  isAnnual,
  className = 'cn_mt-2',
  alignItems = 'center',
  color = 'default',
  highlightPrice = false,
  textAlign = 'center',
  title,
  isTrialPeriodEnabled,
  billingCode,
  billed,
  currencyCode,
  planTermCode,
  price,
  strikeThroughPrice,
  isMediaPurchase,
}) => {
  const { t } = useTranslation();
  const defaultPlanBillsAt = isAnnual
    ? t('common:paywall.plan.billed_yearly')
    : t('common:paywall.plan.billed_monthly');
  const isFreePlan = billingCode === BillingCodes.registration;

  if (isTrialPeriodEnabled && !highlightPrice) {
    return (
      <FlexCol gap="1" justifyContent="left" alignItems={alignItems} className={className}>
        <TextDynamic fontSize={title ? 'bodyLarge' : 'h2'} fontWeight="bold" align={textAlign}>
          {title ?? t('common:paywall.plan.registration.title')}
        </TextDynamic>
        <BodyTextSmall align={textAlign} fontWeight="regular">
          {billingCode === BillingCodes.registration ? defaultPlanBillsAt : billed}
        </BodyTextSmall>
      </FlexCol>
    );
  }

  return (
    <>
      <FlexCol
        alignItems="baseline"
        justifyContent="center"
        gap="2"
        className={`${className} cn_mb-2`}
        data-testid="plan-formatted-price-per-month"
      >
        {strikeThroughPrice > 0 && (
          <FlexRow alignItems="center" gap="1" data-testid="plan-strikethrough-price">
            <FormattedPriceMonth
              price={strikeThroughPrice}
              planCurrencyCode={currencyCode}
              countryCode={countryCode}
              PriceComponent={<H2Text fontWeight="medium" className="cn_line-through" color="disabled" />}
              LabelComponent={<DetailTextLarge color="disabled" />}
              isSingleItemPrice={isMediaPurchase}
            />
          </FlexRow>
        )}
        <FlexRow alignItems="center" gap="1">
          <FormattedPriceMonth
            price={Number(price)}
            planCurrencyCode={currencyCode}
            countryCode={countryCode}
            planTermCode={planTermCode}
            PriceComponent={<H2Text fontWeight="medium" color={color} />}
            LabelComponent={<DetailTextLarge color={color} />}
            isSingleItemPrice={isMediaPurchase}
            displayPlanCurrencyCode
          />
        </FlexRow>
      </FlexCol>
      {!isMediaPurchase && !isFreePlan && (
        <DetailTextLarge align="center" fontWeight="medium" color="disabled">
          {highlightPrice ? defaultPlanBillsAt : billed}
        </DetailTextLarge>
      )}
    </>
  );
};
